import React, { useState, useEffect } from 'react';
import { apiBaseUrl } from '../config.js';

let ioInstance = null;

const Metrics = ({ userToken }) => {
    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    
    const getTomorrowDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const year = tomorrow.getFullYear();
        const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
        const day = String(tomorrow.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [platformMetrics, setPlatformMetrics] = useState(null);
    const [tasaciones, setTasaciones] = useState([]);
    const [tasacionesPagination, setTasacionesPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeUsers, setActiveUsers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('platform');
    // Default startDate to today, endDate to tomorrow (+1 day)
    const [startDate, setStartDate] = useState(getTodayDate());
    const [endDate, setEndDate] = useState(getTomorrowDate());
    const [realtimeNotification, setRealtimeNotification] = useState(null);
    const [leadSources, setLeadSources] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [appointmentsLoading, setAppointmentsLoading] = useState(false);
    const [selectedTasaciones, setSelectedTasaciones] = useState([]);
    
    // WhatsApp Messaging States
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [conversationsLoading, setConversationsLoading] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Settings state
    const [whatsappVerificationEnabled, setWhatsappVerificationEnabled] = useState(true);
    const [settingsLoading, setSettingsLoading] = useState(false);

    useEffect(() => {
        if (activeTab === 'settings' && userToken) {
            fetchSettings();
        }
    }, [activeTab, userToken]);

    useEffect(() => {
        if (userToken) {
            fetchMetrics(1);
            setupSocket();
            const poll = setInterval(fetchRealtimeFallback, 15000);
            return () => {
                clearInterval(poll);
                if (ioInstance) {
                    ioInstance.off('active_users');
                    ioInstance.off('new_tasacion');
                }
            };
        }
    }, [userToken]);
    
    // Note: We removed the auto-refetch on date change to avoid duplicate fetches
    // Users need to click "Filtrar" button to apply date changes

    const setupSocket = async () => {
        try {
            if (!ioInstance) {
                const { io } = await import('socket.io-client');
                // Pass admin token in connection auth
                ioInstance = io(apiBaseUrl, { 
                    transports: ['websocket'], 
                    withCredentials: false,
                    auth: {
                        token: userToken,
                        isAdmin: true
                    }
                });
            }
            
            ioInstance.on('active_users', (count) => {
                setActiveUsers(count);
            });
            
            ioInstance.on('new_tasacion', (data) => {
                setRealtimeNotification({
                    message: `Nueva tasación: ${data.zone || 'N/A'} - ${data.type || 'N/A'}`,
                    timestamp: data.timestamp
                });
                
                // Auto-refresh metrics (reset to page 1)
                fetchMetrics(1);
                
                // Clear notification after 5 seconds
                setTimeout(() => setRealtimeNotification(null), 5000);
            });
        } catch (e) {
        }
    };

    const fetchRealtimeFallback = async () => {
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const res = await fetch(`${apiBaseUrl}/api/stats/realtime`, { headers });
            if (res.ok) {
                const data = await res.json();
                if (typeof data.activeUsers === 'number') setActiveUsers(data.activeUsers);
            }
        } catch (_) {}
    };

    const fetchAppointments = async () => {
        setAppointmentsLoading(true);
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/martillero-appointments?limit=100`, { headers });
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setAppointments(data.appointments);
                }
            }
        } catch (err) {
            console.error('Error fetching appointments:', err);
        } finally {
            setAppointmentsLoading(false);
        }
    };

    // Fetch appointments when tab changes to appointments
    useEffect(() => {
        if (activeTab === 'appointments' && userToken) {
            fetchAppointments();
        }
        // Fetch conversations when tab changes to messages
        if (activeTab === 'messages' && userToken) {
            fetchConversations();
        }
        // Clear selected tasaciones when switching tabs
        if (activeTab !== 'tasaciones') {
            setSelectedTasaciones([]);
        }
    }, [activeTab, userToken]);

    const updateAppointmentStatus = async (appointmentId, newStatus) => {
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            await fetch(`${apiBaseUrl}/api/admin/martillero-appointments/${appointmentId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ status: newStatus })
            });
            
            // Update local state
            setAppointments(prev => prev.map(apt => 
                apt._id === appointmentId ? { ...apt, status: newStatus } : apt
            ));
        } catch (err) {
            console.error('Error updating appointment status:', err);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const toggleTasacionSelection = (tasacion) => {
        setSelectedTasaciones(prev => {
            const isSelected = prev.some(t => t._id === tasacion._id);
            if (isSelected) {
                return prev.filter(t => t._id !== tasacion._id);
            } else {
                return [...prev, tasacion];
            }
        });
    };

    const toggleSelectAll = () => {
        if (selectedTasaciones.length === tasaciones.length) {
            setSelectedTasaciones([]);
        } else {
            setSelectedTasaciones([...tasaciones]);
        }
    };

    // WhatsApp Messaging Functions
    const fetchConversations = async () => {
        setConversationsLoading(true);
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const response = await fetch(`${apiBaseUrl}/api/admin/whatsapp/conversations`, { headers });
            if (response.ok) {
                const data = await response.json();
                setConversations(data.conversations || []);
            }
        } catch (err) {
            console.error('Error fetching conversations:', err);
        } finally {
            setConversationsLoading(false);
        }
    };

    const fetchMessages = async (conversationId) => {
        setMessagesLoading(true);
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const response = await fetch(`${apiBaseUrl}/api/admin/whatsapp/messages/${conversationId}`, { headers });
            if (response.ok) {
                const data = await response.json();
                setMessages(data.messages || []);
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
        } finally {
            setMessagesLoading(false);
        }
    };

    const sendMessage = async () => {
        if (!messageInput.trim() || !selectedConversation) return;

        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const response = await fetch(`${apiBaseUrl}/api/admin/whatsapp/send`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    phone: selectedConversation.phone,
                    message: messageInput
                })
            });

            if (response.ok) {
                const data = await response.json();
                // Add message to local state
                setMessages([...messages, {
                    id: data.messageId,
                    body: messageInput,
                    from: 'admin',
                    timestamp: new Date().toISOString(),
                    status: 'sent'
                }]);
                setMessageInput('');
                // Refresh conversations to update last message
                fetchConversations();
            }
        } catch (err) {
            console.error('Error sending message:', err);
            alert('Error al enviar mensaje');
        }
    };

    const selectConversation = (conversation) => {
        setSelectedConversation(conversation);
        fetchMessages(conversation.id);
    };

    const formatMessageTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 1) return 'Ahora';
        if (diffMins < 60) return `${diffMins}m`;
        
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h`;
        
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays === 1) return 'Ayer';
        if (diffDays < 7) return `${diffDays}d`;
        
        return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
    };

    const filteredConversations = conversations.filter(conv => 
        conv.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.phone?.includes(searchQuery)
    );

    const exportSelectedTasaciones = () => {
        if (selectedTasaciones.length === 0) {
            alert('Por favor selecciona al menos una tasación para exportar');
            return;
        }

        let txtContent = '';
        selectedTasaciones.forEach((tasacion, index) => {
            // Address
            txtContent += tasacion.address || 'N/A';
            txtContent += '\n\n';

            // Venta/Alquiler
            txtContent += tasacion.operation === 'venta' ? 'venta' : tasacion.operation === 'alquiler' ? 'alquiler' : 'N/A';
            txtContent += '\n\n';

            // Price range
            txtContent += tasacion.priceRange || 'N/A';
            txtContent += '\n\n';

            // Phone
            txtContent += tasacion.phone || 'N/A';

            // Add 3 blank lines between tasaciones (except for the last one)
            if (index < selectedTasaciones.length - 1) {
                txtContent += '\n\n\n\n';
            }
        });

        // Create and download file
        const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `tasaciones_export_${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Clear selection after export
        setSelectedTasaciones([]);
    };

    // Fetch only tasaciones table (for pagination)
    const fetchTasaciones = async (page = 1) => {
        try {
            // Build query parameters for date range
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            params.append('page', page);
            params.append('limit', '20');
            const queryString = params.toString() ? `?${params.toString()}` : '';

            // Headers with authentication token
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };

            const tasacionesResponse = await fetch(`${apiBaseUrl}/api/metrics/tasaciones${queryString}`, { headers });
            
            if (tasacionesResponse.ok) {
                const data = await tasacionesResponse.json();
                setTasaciones(data.tasaciones || []);
                setTasacionesPagination(data.pagination || null);
                setCurrentPage(page);
            }
        } catch (err) {
            console.error('Error fetching tasaciones:', err);
        }
    };

    const fetchMetrics = async (page = 1) => {
        try {
            setLoading(true);
            setError(null);

            // Build query parameters for date range
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            params.append('page', page);
            params.append('limit', '20');
            const queryString = params.toString() ? `?${params.toString()}` : '';

            // Headers with authentication token
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };

            const [platformResponse, tasacionesResponse, leadSourcesResponse] = await Promise.all([
                fetch(`${apiBaseUrl}/api/metrics/platform${queryString}`, { headers }),
                fetch(`${apiBaseUrl}/api/metrics/tasaciones${queryString}`, { headers }),
                fetch(`${apiBaseUrl}/api/metrics/lead-sources${queryString}`, { headers })
            ]);

            // Check for authorization errors
            if (platformResponse.status === 403 || platformResponse.status === 401) {
                setError('Acceso denegado. Se requieren privilegios de administrador.');
                setLoading(false);
                return;
            }

            if (platformResponse.ok) setPlatformMetrics(await platformResponse.json());
            
            if (tasacionesResponse.ok) {
                const data = await tasacionesResponse.json();
                setTasaciones(data.tasaciones || []);
                setTasacionesPagination(data.pagination || null);
                setCurrentPage(page);
            }
            
            if (leadSourcesResponse.ok) {
                const data = await leadSourcesResponse.json();
                setLeadSources(data);
            }
            
            await fetchRealtimeFallback();
        } catch (err) {
            setError('Error al cargar las métricas');
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => num?.toLocaleString?.() ?? num;
    const formatCurrency = (num) => `$${Number(num || 0).toLocaleString()}`;
    
    const fetchSettings = async () => {
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, { headers });
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setWhatsappVerificationEnabled(data.settings.whatsappVerificationEnabled);
                }
            }
        } catch (err) {
            console.error('Error fetching settings:', err);
        } finally {
            setSettingsLoading(false);
        }
    };
    
    const toggleWhatsappVerification = async () => {
        const newValue = !whatsappVerificationEnabled;
        
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ whatsappVerificationEnabled: newValue })
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setWhatsappVerificationEnabled(newValue);
                    alert(`✅ WhatsApp verificación ${newValue ? 'activada' : 'desactivada'} correctamente`);
                }
            } else {
                alert('❌ Error al actualizar configuración');
            }
        } catch (err) {
            console.error('Error updating settings:', err);
            alert('❌ Error al actualizar configuración');
        } finally {
            setSettingsLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', fontSize: '18px', color: '#666' }}>
                📊 Cargando métricas...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', fontSize: '18px', color: '#e74c3c' }}>
                ❌ {error}
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
            <h1 style={{ textAlign: 'center', color: '#2c3e50', marginBottom: '20px', fontSize: '28px' }}>
                📊 Dashboard de Métricas
            </h1>

            {/* Real-time notification */}
            {realtimeNotification && (
                <div style={{ 
                    backgroundColor: '#27ae60', 
                    color: 'white', 
                    padding: '12px 20px', 
                    borderRadius: '8px', 
                    marginBottom: '20px', 
                    textAlign: 'center',
                    fontWeight: 'bold',
                    animation: 'fadeIn 0.3s ease-in'
                }}>
                    🔔 {realtimeNotification.message}
                </div>
            )}

            {/* Date Range Filter */}
            <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '12px', 
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
                marginBottom: '20px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '15px',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{ width: '100%', textAlign: 'center', marginBottom: '10px', color: '#7f8c8d', fontSize: '13px' }}>
                    {!startDate && !endDate ? '📊 Mostrando todos los datos' : `📅 Filtrando desde: ${startDate || 'inicio'}${endDate ? ` hasta: ${endDate}` : ' (hasta ahora)'}`}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontWeight: 'bold', color: '#2c3e50' }}>Desde:</label>
                    <input 
                        type="date" 
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        style={{ 
                            padding: '8px 12px', 
                            border: '2px solid #e1e8ed', 
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontWeight: 'bold', color: '#2c3e50' }}>Hasta:</label>
                    <input 
                        type="date" 
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        style={{ 
                            padding: '8px 12px', 
                            border: '2px solid #e1e8ed', 
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <button 
                    onClick={() => fetchMetrics(1)}
                    style={{ 
                        padding: '8px 20px', 
                        backgroundColor: '#3498db', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
                >
                    🔍 Filtrar
                </button>
                <button 
                    onClick={() => {
                        const today = getTodayDate();
                        setStartDate(today);
                        setEndDate(today);
                        setTimeout(fetchMetrics, 100);
                    }}
                    style={{ 
                        padding: '8px 20px', 
                        backgroundColor: '#27ae60', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}
                >
                    📅 Hoy
                </button>
                <button 
                    onClick={() => {
                        setStartDate('');
                        setEndDate('');
                        setTimeout(fetchMetrics, 100);
                    }}
                    style={{ 
                        padding: '8px 20px', 
                        backgroundColor: '#95a5a6', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}
                >
                    ∞ Todo
                </button>
            </div>

            {/* Live tiles row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                    <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Usuarios activos ahora</div>
                    <div style={{ fontSize: '34px', fontWeight: '800', color: '#8e44ad' }}>{activeUsers ?? '—'}</div>
                    <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '6px' }}>Tiempo real (Socket.IO)</div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={() => setActiveTab('platform')} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', backgroundColor: activeTab === 'platform' ? '#3498db' : '#ecf0f1', color: activeTab === 'platform' ? 'white' : '#2c3e50', transition: 'all 0.3s ease' }}>📈 Plataforma</button>
                <button onClick={() => setActiveTab('tasaciones')} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', backgroundColor: activeTab === 'tasaciones' ? '#3498db' : '#ecf0f1', color: activeTab === 'tasaciones' ? 'white' : '#2c3e50', transition: 'all 0.3s ease' }}>🏠 Tasaciones</button>
                <button onClick={() => setActiveTab('leadSources')} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', backgroundColor: activeTab === 'leadSources' ? '#3498db' : '#ecf0f1', color: activeTab === 'leadSources' ? 'white' : '#2c3e50', transition: 'all 0.3s ease' }}>🎯 Fuentes de Leads</button>
                <button onClick={() => setActiveTab('appointments')} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', backgroundColor: activeTab === 'appointments' ? '#3498db' : '#ecf0f1', color: activeTab === 'appointments' ? 'white' : '#2c3e50', transition: 'all 0.3s ease' }}>
                    📅 Citas Martillero
                    {appointments.filter(apt => apt.status === 'pending').length > 0 && activeTab !== 'appointments' && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#e74c3c', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                            {appointments.filter(apt => apt.status === 'pending').length}
                        </span>
                    )}
                </button>
                <button onClick={() => setActiveTab('messages')} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', backgroundColor: activeTab === 'messages' ? '#25D366' : '#ecf0f1', color: activeTab === 'messages' ? 'white' : '#2c3e50', transition: 'all 0.3s ease' }}>
                    💬 Mensajes
                    {conversations.filter(conv => conv.unreadCount > 0).length > 0 && activeTab !== 'messages' && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#e74c3c', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                            {conversations.filter(conv => conv.unreadCount > 0).length}
                        </span>
                    )}
                </button>
                <button onClick={() => setActiveTab('settings')} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', backgroundColor: activeTab === 'settings' ? '#9b59b6' : '#ecf0f1', color: activeTab === 'settings' ? 'white' : '#2c3e50', transition: 'all 0.3s ease' }}>
                    ⚙️ Ajustes
                </button>
            </div>

            {/* Platform Metrics Tab */}
            {activeTab === 'platform' && platformMetrics && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>🏠 Tasaciones Totales</h3>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{formatNumber(platformMetrics.totalValuations)}</div>
                        <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Últimos 30 días: {formatNumber(platformMetrics.recentValuations)}</div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>👤 Usuarios Únicos</h3>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9b59b6' }}>{formatNumber(platformMetrics.uniqueUsersCount)}</div>
                        <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Teléfonos únicos con tasaciones</div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>👥 Usuarios Registrados</h3>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#27ae60' }}>{formatNumber(platformMetrics.totalUsers)}</div>
                        <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Nuevos (30 días): {formatNumber(platformMetrics.newUsers)}</div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>🏙️ Distribución por Zona</h3>
                        <div style={{ marginBottom: '10px' }}><span style={{ color: '#3498db' }}>CABA:</span> {formatNumber(platformMetrics.cabaValuations)}</div>
                        <div><span style={{ color: '#e67e22' }}>Provincia:</span> {formatNumber(platformMetrics.provinciaValuations)}</div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>💰 Precio Promedio</h3>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f39c12' }}>{platformMetrics.avgPriceRange}</div>
                        <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Rango promedio por tasación</div>
                    </div>
                </div>
            )}

            {/* Tasaciones Tab */}
            {activeTab === 'tasaciones' && (
                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ color: '#2c3e50', margin: 0, fontSize: '20px' }}>🏠 Últimas Tasaciones</h3>
                        {selectedTasaciones.length > 0 && (
                            <button
                                onClick={exportSelectedTasaciones}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#27ae60',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                📥 Exportar ({selectedTasaciones.length})
                            </button>
                        )}
                    </div>
                    {tasaciones.length > 0 ? (
                        <>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '40px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTasaciones.length === tasaciones.length}
                                                    onChange={toggleSelectAll}
                                                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                />
                                            </th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Fecha</th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Dirección</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Operación</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Rango de Precio</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Teléfono</th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>IP</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tasaciones.map((tasacion, index) => {
                                            const isSelected = selectedTasaciones.some(t => t._id === tasacion._id);
                                            return (
                                            <tr key={index} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: isSelected ? '#e8f5e9' : 'transparent' }}>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleTasacionSelection(tasacion)}
                                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                    />
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '12px', color: '#7f8c8d' }}>
                                                    {tasacion.timestamp ? new Date(tasacion.timestamp).toLocaleDateString('es-ES', { 
                                                        year: 'numeric',
                                                        month: 'short', 
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : 'N/A'}
                                                </td>
                                                <td style={{ padding: '12px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tasacion.address}>
                                                    {tasacion.address}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <span style={{
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        backgroundColor: tasacion.operation === 'alquiler' ? '#e3f2fd' : '#f3e5f5',
                                                        color: tasacion.operation === 'alquiler' ? '#1976d2' : '#7b1fa2'
                                                    }}>
                                                        {tasacion.operation === 'alquiler' ? '🏠 Alquiler' : '💰 Venta'}
                                                    </span>
                                                </td>
                                                <td style={{ 
                                                    padding: '12px', 
                                                    textAlign: 'center', 
                                                    color: '#27ae60', 
                                                    fontWeight: 'bold', 
                                                    fontSize: tasacion.operation === 'alquiler' ? '12px' : '13px',
                                                    minWidth: '200px',
                                                    lineHeight: '1.4'
                                                }}>
                                                    {tasacion.priceRange}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    {tasacion.phone}
                                                </td>
                                                <td style={{ padding: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tasacion.email}>
                                                    {tasacion.email}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#7f8c8d', fontFamily: 'monospace' }}>
                                                    {tasacion.ip || 'N/A'}
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Pagination Controls */}
                            {tasacionesPagination && tasacionesPagination.totalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', gap: '10px' }}>
                                    <button
                                        onClick={() => fetchTasaciones(currentPage - 1)}
                                        disabled={!tasacionesPagination.hasPrevPage}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: tasacionesPagination.hasPrevPage ? '#3498db' : '#ecf0f1',
                                            color: tasacionesPagination.hasPrevPage ? 'white' : '#95a5a6',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: tasacionesPagination.hasPrevPage ? 'pointer' : 'not-allowed',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        ← Anterior
                                    </button>
                                    
                                    <span style={{ color: '#2c3e50', fontSize: '14px', fontWeight: 'bold' }}>
                                        Página {tasacionesPagination.currentPage} de {tasacionesPagination.totalPages}
                                        <span style={{ color: '#7f8c8d', fontSize: '12px', marginLeft: '8px' }}>
                                            ({tasacionesPagination.totalCount} total)
                                        </span>
                                    </span>
                                    
                                    <button
                                        onClick={() => fetchTasaciones(currentPage + 1)}
                                        disabled={!tasacionesPagination.hasNextPage}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: tasacionesPagination.hasNextPage ? '#3498db' : '#ecf0f1',
                                            color: tasacionesPagination.hasNextPage ? 'white' : '#95a5a6',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: tasacionesPagination.hasNextPage ? 'pointer' : 'not-allowed',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Siguiente →
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '40px' }}>
                            No hay tasaciones disponibles
                        </div>
                    )}
                </div>
            )}

            {/* Lead Sources Tab */}
            {activeTab === 'leadSources' && leadSources && (
                <div>
                    {/* Summary Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>👥 Visitantes Totales</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{formatNumber(leadSources.totals.totalVisitors)}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Visitantes únicos</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>✅ Conversiones</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#27ae60' }}>{formatNumber(leadSources.totals.totalConversions)}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Tasaciones completadas</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>👤 Usuarios Únicos</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#e67e22' }}>{formatNumber(leadSources.totals.totalUniqueUserConversions || 0)}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Conversiones únicas</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>📊 Tasa de Conversión</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9b59b6' }}>{leadSources.totals.avgConversionRate}%</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Promedio general</div>
                        </div>
                    </div>

                    {/* Lead Sources Table */}
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '20px', fontSize: '20px' }}>🎯 Fuentes de Leads por Performance</h3>
                        {leadSources.leadSources && leadSources.leadSources.length > 0 ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>Referencia</th>
                                            <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>Fuente</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Visitantes</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Conversiones</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Usuarios Únicos</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Tasa Conv.</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Páginas/Visita</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leadSources.leadSources.map((source, index) => {
                                            // Source name formatting with emoji
                                            const getSourceDisplay = (sourceName) => {
                                                const sourceMap = {
                                                    'google_ads': '🎯 Google Ads',
                                                    'tiktok_ads': '🎵 TikTok Ads',
                                                    'facebook_ads': '👥 Facebook Ads',
                                                    'instagram_ads': '📷 Instagram Ads',
                                                    'google_organic': '🔍 Google Orgánico',
                                                    'facebook_organic': '👥 Facebook Orgánico',
                                                    'instagram_organic': '📷 Instagram Orgánico',
                                                    'tiktok_organic': '🎵 TikTok Orgánico',
                                                    'youtube': '📺 YouTube',
                                                    'referral': '🔗 Referencia',
                                                    'organic': '🌐 Directo/Orgánico',
                                                    'unknown': '❓ Desconocido'
                                                };
                                                return sourceMap[sourceName] || `📌 ${sourceName}`;
                                            };

                                            // Get clean reference name (platform only)
                                            const getReference = (sourceName) => {
                                                const referenceMap = {
                                                    'google_ads': 'Google',
                                                    'tiktok_ads': 'TikTok',
                                                    'facebook_ads': 'Facebook',
                                                    'instagram_ads': 'Instagram',
                                                    'google_organic': 'Google',
                                                    'facebook_organic': 'Facebook',
                                                    'instagram_organic': 'Instagram',
                                                    'tiktok_organic': 'TikTok',
                                                    'youtube': 'YouTube',
                                                    'referral': 'Referencia',
                                                    'organic': 'Directo',
                                                    'unknown': 'Desconocido'
                                                };
                                                return referenceMap[sourceName] || sourceName;
                                            };

                                            const convRate = source.conversionRate || 0;
                                            const convRateColor = convRate > 10 ? '#27ae60' : convRate > 5 ? '#f39c12' : '#e74c3c';

                                            return (
                                                <tr key={index} style={{ borderBottom: '1px solid #dee2e6', transition: 'background-color 0.2s' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                                                    <td style={{ padding: '15px', fontWeight: '700', color: '#2c3e50', fontSize: '16px' }}>{getReference(source.source)}</td>
                                                    <td style={{ padding: '15px', fontWeight: '600', color: '#7f8c8d' }}>{getSourceDisplay(source.source)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#3498db', fontWeight: '600' }}>{formatNumber(source.visitors)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#27ae60', fontWeight: '600' }}>{formatNumber(source.conversions)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#e67e22', fontWeight: '600' }}>{formatNumber(source.uniqueUserConversions || 0)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', fontWeight: '700', color: convRateColor }}>
                                                        {convRate}%
                                                    </td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#7f8c8d' }}>{source.avgPageViews}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '40px' }}>
                                No hay datos de fuentes de leads disponibles
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
                <div>
                    {/* Appointment Stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '30px' }}>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Total Citas</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{appointments.length}</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Pendientes</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f39c12' }}>{appointments.filter(apt => apt.status === 'pending').length}</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Contactados</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{appointments.filter(apt => apt.status === 'contacted').length}</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Completados</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#27ae60' }}>{appointments.filter(apt => apt.status === 'completed').length}</div>
                        </div>
                    </div>

                    {/* Appointments Table */}
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '20px', fontSize: '20px' }}>📅 Citas con Martillero</h3>
                        
                        {appointmentsLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                                ⏳ Cargando citas...
                            </div>
                        ) : appointments.length > 0 ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '200px' }}>📍 Dirección</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '120px' }}>📞 Teléfono</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '150px' }}>📧 Email</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>🏠 Tipo</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>Operación</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>💰 Valor Est.</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '140px' }}>📅 Fecha</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '140px' }}>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appointments.map((apt, index) => {
                                            const statusColors = {
                                                pending: '#f39c12',
                                                contacted: '#3498db',
                                                completed: '#27ae60',
                                                cancelled: '#e74c3c'
                                            };
                                            const statusLabels = {
                                                pending: 'Pendiente',
                                                contacted: 'Contactado',
                                                completed: 'Completado',
                                                cancelled: 'Cancelado'
                                            };
                                            
                                            return (
                                                <tr key={apt._id} style={{ borderBottom: '1px solid #e1e8ed', backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>{apt.address}</td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                                        <a 
                                                            href={`https://wa.me/${apt.phone?.replace(/\D/g, '')}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: '#25D366', fontWeight: 'bold', textDecoration: 'none' }}
                                                        >
                                                            {apt.phone}
                                                        </a>
                                                    </td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>{apt.email || '-'}</td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>{apt.propertyType || '-'}</td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                                        {apt.operation === 'venta' ? 'Venta' : apt.operation === 'alquiler' ? 'Alquiler' : '-'}
                                                    </td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>{apt.estimatedValue || '-'}</td>
                                                    <td style={{ padding: '12px', fontSize: '13px', color: '#7f8c8d' }}>{formatDate(apt.createdAt)}</td>
                                                    <td style={{ padding: '12px' }}>
                                                        <select
                                                            value={apt.status}
                                                            onChange={(e) => updateAppointmentStatus(apt._id, e.target.value)}
                                                            style={{
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                border: '2px solid #e1e8ed',
                                                                backgroundColor: statusColors[apt.status] || '#95a5a6',
                                                                color: 'white',
                                                                fontWeight: 'bold',
                                                                fontSize: '13px',
                                                                cursor: 'pointer',
                                                                width: '130px'
                                                            }}
                                                        >
                                                            <option value="pending">Pendiente</option>
                                                            <option value="contacted">Contactado</option>
                                                            <option value="completed">Completado</option>
                                                            <option value="cancelled">Cancelado</option>
                                                        </select>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '40px' }}>
                                📅 No hay citas registradas todavía
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Messages Tab - WhatsApp Interface */}
            {activeTab === 'messages' && (
                <div style={{ 
                    backgroundColor: 'white', 
                    borderRadius: '12px', 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)', 
                    overflow: 'hidden',
                    height: '700px',
                    display: 'flex'
                }}>
                    {/* Left Side - Conversations List */}
                    <div style={{ 
                        width: '380px', 
                        borderRight: '1px solid #e9ecef',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#f8f9fa'
                    }}>
                        {/* Header */}
                        <div style={{ 
                            padding: '20px', 
                            backgroundColor: '#25D366',
                            color: 'white',
                            borderBottom: '1px solid rgba(0,0,0,0.1)'
                        }}>
                            <h3 style={{ margin: '0 0 15px 0', fontSize: '20px', fontWeight: 'bold' }}>
                                💬 Conversaciones
                            </h3>
                            {/* Search */}
                            <input 
                                type="text"
                                placeholder="Buscar conversación..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 15px',
                                    border: 'none',
                                    borderRadius: '20px',
                                    fontSize: '14px',
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    outline: 'none'
                                }}
                            />
                        </div>

                        {/* Conversations List */}
                        <div style={{ 
                            flex: 1, 
                            overflowY: 'auto',
                            backgroundColor: 'white'
                        }}>
                            {conversationsLoading ? (
                                <div style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
                                    Cargando conversaciones...
                                </div>
                            ) : filteredConversations.length > 0 ? (
                                filteredConversations.map((conv) => (
                                    <div
                                        key={conv.id}
                                        onClick={() => selectConversation(conv)}
                                        style={{
                                            padding: '16px 20px',
                                            borderBottom: '1px solid #f0f0f0',
                                            cursor: 'pointer',
                                            backgroundColor: selectedConversation?.id === conv.id ? '#e7f3ff' : 'white',
                                            transition: 'background-color 0.2s',
                                            display: 'flex',
                                            gap: '12px',
                                            alignItems: 'flex-start'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (selectedConversation?.id !== conv.id) {
                                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (selectedConversation?.id !== conv.id) {
                                                e.currentTarget.style.backgroundColor = 'white';
                                            }
                                        }}
                                    >
                                        {/* Avatar */}
                                        <div style={{
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            backgroundColor: '#25D366',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            flexShrink: 0
                                        }}>
                                            {conv.name ? conv.name.charAt(0).toUpperCase() : '👤'}
                                        </div>

                                        {/* Conversation Info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '4px'
                                            }}>
                                                <div style={{ 
                                                    fontWeight: conv.unreadCount > 0 ? '700' : '600',
                                                    fontSize: '15px',
                                                    color: '#2c3e50',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {conv.name || conv.phone}
                                                </div>
                                                <div style={{ 
                                                    fontSize: '12px',
                                                    color: '#7f8c8d',
                                                    flexShrink: 0,
                                                    marginLeft: '8px'
                                                }}>
                                                    {formatMessageTime(conv.lastMessageTime)}
                                                </div>
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '4px' }}>
                                                {conv.phone}
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <div style={{
                                                    fontSize: '14px',
                                                    color: conv.unreadCount > 0 ? '#2c3e50' : '#95a5a6',
                                                    fontWeight: conv.unreadCount > 0 ? '600' : '400',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flex: 1
                                                }}>
                                                    {conv.lastMessage || 'Sin mensajes'}
                                                </div>
                                                {conv.unreadCount > 0 && (
                                                    <div style={{
                                                        backgroundColor: '#25D366',
                                                        color: 'white',
                                                        borderRadius: '12px',
                                                        padding: '2px 8px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold',
                                                        marginLeft: '8px',
                                                        flexShrink: 0
                                                    }}>
                                                        {conv.unreadCount}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ 
                                    padding: '60px 20px', 
                                    textAlign: 'center',
                                    color: '#95a5a6'
                                }}>
                                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
                                    <div style={{ fontSize: '16px', fontWeight: '500' }}>
                                        {searchQuery ? 'No se encontraron conversaciones' : 'No hay conversaciones todavía'}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Side - Chat Area */}
                    <div style={{ 
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#e5ddd5'
                    }}>
                        {selectedConversation ? (
                            <>
                                {/* Chat Header */}
                                <div style={{
                                    padding: '16px 20px',
                                    backgroundColor: '#f0f2f5',
                                    borderBottom: '1px solid #d1d7db',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}>
                                    <div style={{
                                        width: '42px',
                                        height: '42px',
                                        borderRadius: '50%',
                                        backgroundColor: '#25D366',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        fontWeight: 'bold'
                                    }}>
                                        {selectedConversation.name ? selectedConversation.name.charAt(0).toUpperCase() : '👤'}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '600', fontSize: '16px', color: '#2c3e50' }}>
                                            {selectedConversation.name || selectedConversation.phone}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#667781' }}>
                                            {selectedConversation.phone}
                                        </div>
                                    </div>
                                </div>

                                {/* Messages Area */}
                                <div style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    padding: '20px',
                                    backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h100v100H0z\' fill=\'%23e5ddd5\'/%3E%3Cpath d=\'M20 20h60v60H20z\' fill=\'none\' stroke=\'%23d1d7db\' stroke-width=\'0.5\' opacity=\'0.1\'/%3E%3C/svg%3E")'
                                }}>
                                    {messagesLoading ? (
                                        <div style={{ textAlign: 'center', color: '#667781', paddingTop: '40px' }}>
                                            Cargando mensajes...
                                        </div>
                                    ) : messages.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {messages.map((msg, index) => {
                                                const isFromAdmin = msg.from === 'admin';
                                                return (
                                                    <div
                                                        key={msg.id || index}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: isFromAdmin ? 'flex-end' : 'flex-start',
                                                            marginBottom: '4px'
                                                        }}
                                                    >
                                                        <div style={{
                                                            maxWidth: '65%',
                                                            backgroundColor: isFromAdmin ? '#d9fdd3' : 'white',
                                                            borderRadius: '8px',
                                                            padding: '8px 12px',
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                            position: 'relative'
                                                        }}>
                                                            <div style={{
                                                                fontSize: '14px',
                                                                color: '#2c3e50',
                                                                wordWrap: 'break-word',
                                                                marginBottom: '4px'
                                                            }}>
                                                                {msg.body}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '11px',
                                                                color: '#667781',
                                                                textAlign: 'right',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'flex-end',
                                                                gap: '4px'
                                                            }}>
                                                                {formatMessageTime(msg.timestamp)}
                                                                {isFromAdmin && (
                                                                    <span style={{ color: msg.status === 'read' ? '#53bdeb' : '#95a5a6' }}>
                                                                        {msg.status === 'read' ? '✓✓' : '✓'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            color: '#667781',
                                            paddingTop: '60px'
                                        }}>
                                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
                                            <div>No hay mensajes en esta conversación</div>
                                        </div>
                                    )}
                                </div>

                                {/* Message Input */}
                                <div style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#f0f2f5',
                                    borderTop: '1px solid #d1d7db',
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center'
                                }}>
                                    <input
                                        type="text"
                                        placeholder="Escribe un mensaje..."
                                        value={messageInput}
                                        onChange={(e) => setMessageInput(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                sendMessage();
                                            }
                                        }}
                                        style={{
                                            flex: 1,
                                            padding: '12px 16px',
                                            border: 'none',
                                            borderRadius: '24px',
                                            fontSize: '15px',
                                            outline: 'none',
                                            backgroundColor: 'white'
                                        }}
                                    />
                                    <button
                                        onClick={sendMessage}
                                        disabled={!messageInput.trim()}
                                        style={{
                                            padding: '12px 24px',
                                            backgroundColor: messageInput.trim() ? '#25D366' : '#ccc',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '24px',
                                            cursor: messageInput.trim() ? 'pointer' : 'not-allowed',
                                            fontSize: '15px',
                                            fontWeight: '600',
                                            transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (messageInput.trim()) {
                                                e.target.style.backgroundColor = '#1fa855';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (messageInput.trim()) {
                                                e.target.style.backgroundColor = '#25D366';
                                            }
                                        }}
                                    >
                                        Enviar
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: '#667781',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <div style={{ fontSize: '72px', marginBottom: '20px' }}>💬</div>
                                <div style={{ fontSize: '20px', fontWeight: '500', marginBottom: '8px' }}>
                                    WhatsApp Business
                                </div>
                                <div style={{ fontSize: '14px', color: '#95a5a6' }}>
                                    Selecciona una conversación para comenzar
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Settings Tab - Admin Only */}
            {activeTab === 'settings' && (
                <div style={{ 
                    backgroundColor: 'white', 
                    padding: '30px', 
                    borderRadius: '12px', 
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    maxWidth: '800px',
                    margin: '0 auto'
                }}>
                    <h2 style={{ 
                        fontSize: '24px', 
                        fontWeight: '700', 
                        color: '#2c3e50', 
                        marginBottom: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        ⚙️ Ajustes del Sistema
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#e74c3c',
                            backgroundColor: '#ffe5e5',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            marginLeft: 'auto'
                        }}>
                            🔒 SOLO ADMIN
                        </span>
                    </h2>

                    {/* WhatsApp Verification Toggle */}
                    <div style={{ 
                        border: '2px solid #e1e8ed', 
                        borderRadius: '12px', 
                        padding: '25px',
                        marginBottom: '20px',
                        backgroundColor: '#f8f9fa'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '15px'
                        }}>
                            <div style={{ flex: 1, minWidth: '250px' }}>
                                <h3 style={{ 
                                    fontSize: '18px', 
                                    fontWeight: '600', 
                                    color: '#2c3e50', 
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    📱 Verificación de WhatsApp
                                </h3>
                                <p style={{ 
                                    fontSize: '14px', 
                                    color: '#7f8c8d', 
                                    margin: 0,
                                    lineHeight: '1.5'
                                }}>
                                    Cuando está activada, el sistema verifica que el número de teléfono tenga WhatsApp antes de permitir realizar una tasación.
                                    {!whatsappVerificationEnabled && (
                                        <span style={{ 
                                            display: 'block', 
                                            marginTop: '8px', 
                                            color: '#e67e22', 
                                            fontWeight: '600' 
                                        }}>
                                            ⚠️ Desactivado: Los usuarios pueden tasar sin verificación de WhatsApp
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    color: whatsappVerificationEnabled ? '#27ae60' : '#95a5a6' 
                                }}>
                                    {whatsappVerificationEnabled ? 'Activado' : 'Desactivado'}
                                </span>
                                <button
                                    onClick={toggleWhatsappVerification}
                                    disabled={settingsLoading}
                                    style={{
                                        position: 'relative',
                                        width: '60px',
                                        height: '32px',
                                        backgroundColor: whatsappVerificationEnabled ? '#27ae60' : '#95a5a6',
                                        borderRadius: '16px',
                                        border: 'none',
                                        cursor: settingsLoading ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.3s ease',
                                        outline: 'none',
                                        opacity: settingsLoading ? 0.6 : 1
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute',
                                        top: '4px',
                                        left: whatsappVerificationEnabled ? '32px' : '4px',
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        transition: 'left 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}></div>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Info box */}
                    <div style={{ 
                        backgroundColor: '#e8f4fd', 
                        border: '1px solid #3498db', 
                        borderRadius: '8px', 
                        padding: '15px',
                        fontSize: '14px',
                        color: '#2c3e50'
                    }}>
                        <strong>ℹ️ Nota:</strong> Los cambios en esta configuración se aplican inmediatamente a todas las nuevas tasaciones.
                    </div>
                </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <button onClick={() => fetchMetrics(1)} style={{ padding: '12px 24px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', transition: 'background-color 0.3s ease' }} onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'} onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}>
                    🔄 Actualizar Métricas
                </button>
            </div>
        </div>
    );
};

export default Metrics; 